import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { appColors } from '../../Config/Theme';

export type SaveBeforeLeaveState = 'idle' | 'saving' | 'error' | 'offline';

interface HeadcountSaveBeforeLeaveDialogProps {
  open: boolean;
  state: SaveBeforeLeaveState;
  pendingCount: number;
  errorMessage?: string;
  onRetry: () => void;
  onStay: () => void;
  onDiscard: () => void;
}

const HeadcountSaveBeforeLeaveDialog: React.FC<HeadcountSaveBeforeLeaveDialogProps> = ({
  open,
  state,
  pendingCount,
  errorMessage,
  onRetry,
  onStay,
  onDiscard,
}) => {
  const isSaving = state === 'saving';
  const isError = state === 'error';
  const isOffline = state === 'offline';

  const title = isError
    ? 'Unable to save changes'
    : isOffline
      ? 'You are offline'
      : 'Saving headcount changes';

  const body = isError
    ? 'Some headcount updates could not be saved. Stay on this page and try again, or discard the pending local changes.'
    : isOffline
      ? 'Reconnect to save pending headcount changes before leaving this page.'
      : `Saving ${pendingCount} pending ${pendingCount === 1 ? 'update' : 'updates'} before leaving this page.`;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      aria-labelledby="save-before-leave-title"
      onClose={(_e, reason) => {
        if (isSaving) return;
        if (reason === 'backdropClick') return;
        onStay();
      }}
    >
      <DialogTitle id="save-before-leave-title" sx={{ fontWeight: 700, color: appColors.text.heading }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isSaving && <CircularProgress size={24} />}
          <Typography
            variant="body2"
            sx={{ color: appColors.text.body }}
            aria-live="polite"
          >
            {body}
          </Typography>
        </Box>
        {isError && errorMessage && (
          <Typography
            variant="caption"
            sx={{ color: appColors.status.error, mt: 1, display: 'block' }}
            role="alert"
          >
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {isSaving ? null : (
          <>
            {(isError || isOffline) && (
              <Button
                variant="text"
                color="error"
                size="small"
                onClick={onDiscard}
                sx={{ mr: 'auto', fontWeight: 600 }}
              >
                Discard and Leave
              </Button>
            )}
            {isError && (
              <Button variant="contained" onClick={onRetry} sx={{ fontWeight: 700 }}>
                Retry Save
              </Button>
            )}
            <Button
              variant={isError || isOffline ? 'outlined' : 'text'}
              onClick={onStay}
              sx={{ fontWeight: 600 }}
            >
              Stay on Headcount
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default HeadcountSaveBeforeLeaveDialog;
